<svelte:options runes={false} />
<script>
  // The Strategy workspace header's ORG chooser (Paper & Live). Unlike the Portfolio's multi-select, this is
  // SINGLE-select: a strategy is monitored one participating org at a time (the org scopes every tab + the
  // headline NAV). Exactly one org is always selected; the current one cannot be deselected. With a single
  // participating org the control is a non-interactive label (one org, locked selected).
  //
  // It is a controlled component: `selected` (a single orgId) is the current selection from the parent; picking
  // a new org dispatches a `change` event with the chosen orgId. The parent maps that onto the global
  // selectedOrgId store (the single source of truth), which re-scopes the workspace.
  import { onDestroy, createEventDispatcher } from 'svelte';

  export let selected = null;                // the currently-selected orgId (from the parent / store)
  export let available = [];                 // [{ orgId, orgName }] the strategy's participating orgs
  export let disabled = false;

  const dispatch = createEventDispatcher();

  let open = false;
  let rootEl;

  const labelFor = (orgId) => available.find((o) => o.orgId === orgId)?.orgName || orgId;

  $: single = available.length <= 1;         // one participating org → locked, non-interactive

  function choose(orgId) {
    open = false;
    if (orgId === selected) return;
    dispatch('change', orgId);               // parent updates the store → workspace re-scopes
  }

  function toggleOpen() {
    if (disabled || single) return;
    open = !open;
  }

  function onWindowPointerDown(event) {
    if (rootEl && !rootEl.contains(event.target)) open = false;
  }
  $: if (typeof window !== 'undefined') {
    if (open) window.addEventListener('pointerdown', onWindowPointerDown, true);
    else window.removeEventListener('pointerdown', onWindowPointerDown, true);
  }
  onDestroy(() => {
    if (typeof window !== 'undefined') window.removeEventListener('pointerdown', onWindowPointerDown, true);
  });
</script>

<div class="oss-root" class:disabled bind:this={rootEl}>
  <span class="oss-prefix">Org:</span>
  {#if available.length === 0}
    <span class="oss-chip oss-placeholder">—</span>
  {:else if single}
    <!-- One participating org: a locked label, always selected, not deselectable. -->
    <span class="oss-chip oss-locked" title="The only participating org — always selected">{labelFor(selected ?? available[0].orgId)}</span>
  {:else}
    <button
      type="button"
      class="oss-trigger"
      class:open
      aria-haspopup="listbox"
      aria-expanded={open}
      {disabled}
      on:click|stopPropagation={toggleOpen}
    >
      <span class="oss-chip-label">{labelFor(selected)}</span>
      <span class="oss-caret" aria-hidden="true">▾</span>
    </button>

    {#if open}
      <div class="oss-dropdown" role="listbox" aria-label="Select participating org">
        {#each available as org (org.orgId)}
          {@const sel = org.orgId === selected}
          <!-- svelte-ignore a11y_click_events_have_key_events a11y_interactive_supports_focus -->
          <div
            class="oss-option"
            class:selected={sel}
            role="option"
            aria-selected={sel}
            on:click={() => choose(org.orgId)}
          >
            <span class="oss-check" aria-hidden="true">{sel ? '✓' : ''}</span>
            <span class="oss-option-label">{org.orgName || org.orgId}</span>
          </div>
        {/each}
      </div>
    {/if}
  {/if}
</div>

<style>
  /* Uses the app's DARK theme tokens (theme.css): --bg-*, --text-*, --border-*, --color-primary. */
  .oss-root { position: relative; display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.85rem; }
  .oss-root.disabled { opacity: 0.6; pointer-events: none; }
  .oss-prefix { color: var(--text-secondary, #8a8b8d); font-weight: 600; }
  .oss-chip {
    display: inline-flex; align-items: center; padding: 0.1rem 0.5rem; border-radius: 999px;
    background: var(--color-primary-alpha-20, rgba(0, 212, 255, 0.2)); color: var(--text-primary, #ffffff);
    font-weight: 600; white-space: nowrap;
  }
  .oss-placeholder { background: var(--bg-quaternary, #212226); color: var(--text-secondary, #8a8b8d); }
  .oss-locked { cursor: default; }
  .oss-trigger {
    display: inline-flex; align-items: center; gap: 0.25rem; padding: 0.1rem 0.45rem;
    border: 1px solid var(--border-secondary, #2a2b2e); border-radius: 999px; background: var(--bg-tertiary, #1a1b1e);
    color: var(--text-primary, #ffffff); cursor: pointer; font-weight: 600; white-space: nowrap;
  }
  .oss-trigger.open { border-color: var(--color-primary, #00d4ff); }
  .oss-caret { color: var(--text-secondary, #8a8b8d); }
  .oss-dropdown {
    position: absolute; top: calc(100% + 4px); left: 0; z-index: 30; min-width: 180px;
    background: var(--bg-secondary, #111214); border: 1px solid var(--border-secondary, #2a2b2e); border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5); padding: 0.3rem;
  }
  .oss-option {
    display: flex; align-items: center; gap: 0.5rem; padding: 0.35rem 0.4rem; border-radius: 6px; cursor: pointer;
    color: var(--text-primary, #ffffff);
  }
  .oss-option:hover { background: var(--bg-tertiary, #1a1b1e); }
  .oss-option.selected { font-weight: 600; }
  .oss-check { width: 1rem; color: var(--color-primary, #00d4ff); }
  .oss-option-label { color: var(--text-primary, #ffffff); }
</style>
